import { Path } from 'opentype.js';

import { MediaText, ReflowGroup, ReflowRow, Spread } from 'editor/src/store/design/types';
import getReflowTextStyleKey from 'editor/src/store/design/util/getReflowTextStyleKey';
import {
  getReflowBounds,
  getReflowMemberLeft,
  getReflowMemberVerticalBounds,
  isReflowMember,
  moveReflowMember,
  ReflowMember,
} from 'editor/src/store/design/util/reflowMember';

import parseSvgPathToCommands from 'editor/src/util/parseSvgPathToCommands';

export interface ReflowElementUpdate {
  uuid: number;
  y?: number;
  x?: number;
  y1?: number;
  y2?: number;
  x1?: number;
  x2?: number;
}

export interface ReflowRowShiftUpdate {
  /**
   * Scoping the update by groupId is load-bearing: two reflow groups on the
   * same spread can carry rows with identical `rowId` (clusterIntoRows
   * derives stable ids from the contained uuids, so a re-use of the same
   * uuids in two groups would produce duplicate row ids). Without groupId
   * the reducer would map both rows to the same shift in a single Map.
   */
  groupId: string;
  rowId: string;
  shiftY?: number;
  elementShiftsX?: Record<string, number>;
}

export interface ReflowState {
  /** Element uuids that should be marked `hidden:true` because their row collapsed. */
  hiddenUuids: Set<number>;
  /** Per-element y mutations to bring the spread into the reflowed state. */
  elementUpdates: ReflowElementUpdate[];
  /** Per-row shiftY mutations recording the new applied shift for each row. */
  rowShiftUpdates: ReflowRowShiftUpdate[];
}

function isMediaText(element: ReflowMember): element is MediaText {
  return element.type === 'text';
}

export function getPathVisualWidth(element: MediaText): number | undefined {
  if (!element.path) {
    return undefined;
  }

  const commands = parseSvgPathToCommands(element.path);
  if (!commands.length) {
    return undefined;
  }

  const path = new Path();
  path.commands = commands;
  const bbox = path.getBoundingBox();
  const width = bbox.x2 * (element.sx ?? 1);

  return Number.isFinite(width) && width > 0 ? width : undefined;
}

export function getHorizontalReflowWidth(element: MediaText, row: ReflowRow): number {
  // Advance by the element's measured content width, which includes the glyph's
  // side bearing. A "|" divider's bare stroke (the saved SVG path bbox) is ~0.71mm,
  // but its rendered footprint (element.width) is ~1.26mm — the extra is the
  // bearing that visually separates it from neighbours. The editor re-bakes the
  // path WITH the bearing (~1.26) while the storefront personalizer renders the
  // protected font from the saved stroke-only path (~0.71); packing by the path
  // bbox therefore advances differently in the two (touching vs spaced). Using
  // element.width — identical in both — packs the next element past the bearing in
  // both, so editor and personalizer render the same "Mobile | Phone" (GAPI-39275).
  // Fall back to the path bbox only when width is missing/collapsed.
  const width = element.width > 0 ? element.width : getPathVisualWidth(element) ?? element.width;
  const offset = row.elementWidthOffsets?.[String(element.uuid)];
  if (!row.dividerUuids?.includes(element.uuid) && offset?.style === getReflowTextStyleKey(element)) {
    return Math.max(0, width + offset.value);
  }
  return width;
}

/**
 * Content width used when packing a row horizontally. Text keeps its own
 * measured/path-aware width (getHorizontalReflowWidth, text-only logic); a
 * non-text member packs by its bounding-box width instead.
 */
export function getReflowMemberPackWidth(element: ReflowMember, row: ReflowRow): number {
  if (isMediaText(element)) {
    return getHorizontalReflowWidth(element, row);
  }
  const bounds = getReflowBounds(element);
  return bounds.right - bounds.left;
}

/**
 * An element contributes visible content to its row when it is neither hidden
 * by a Condition nor blank. Single source of truth for both the row-collapse
 * test and the divider neighbour-presence test. Non-text members carry no
 * text, so they are always "present" for packing purposes unless a Condition
 * hides them — but see isRowCollapsed below: they never count toward keeping
 * a row alive.
 */
function hasVisibleContent(element: ReflowMember, conditionHidden: Set<number>): boolean {
  if (conditionHidden.has(element.uuid)) {
    return false;
  }
  return isMediaText(element) ? (element.extra.text ?? '').trim().length > 0 : true;
}

function isRowCollapsed(row: ReflowRow, presentElements: ReflowMember[], conditionHidden: Set<number>): boolean {
  if (!row.optional || presentElements.length === 0) {
    return false;
  }
  // Dividers are separators, not content — they must not keep a row alive (their
  // text is e.g. "|", always non-blank), so exclude them from the editable check.
  // Non-text members (a decorative line/shape/image) are excluded the same way:
  // they can never keep a row alive on their own.
  const dividers = new Set(row.dividerUuids ?? []);
  const editable = presentElements.filter(
    (e): e is MediaText => isMediaText(e) && !e.personalizationLocked && !dividers.has(e.uuid),
  );
  if (editable.length > 0) {
    return editable.every((e) => !hasVisibleContent(e, conditionHidden));
  }
  // No editable members — only collapse if all members are condition-hidden.
  return presentElements.every((e) => conditionHidden.has(e.uuid));
}

interface RowSnapshot {
  row: ReflowRow;
  present: ReflowMember[];
  collapsed: boolean;
  currentShift: number;
  authoredTop: number;
  authoredBottom: number;
}

/**
 * Pure reflow computation. Given a spread and the set of uuids already hidden
 * by Conditions, returns:
 *   - which extra uuids should be hidden (because their reflow row collapsed)
 *   - the y mutations each affected element needs (to shift rows up over
 *     collapsed predecessors, or shift them back down when a predecessor
 *     reopens)
 *   - the new `shiftY` value to record on each affected row
 *
 * Coordinate convention: `element.y` in the data is the CURRENT rendered
 * position. `row.shiftY` records how much this row's elements have been
 * shifted from their authored position. Authored y is recoverable as
 * `element.y - row.shiftY`. The function is idempotent: re-running on an
 * already-reflowed spread returns empty updates unless the desired state
 * changed (because the slot computation uses *authored* positions, which are
 * invariant under prior reflow application).
 *
 * Vertical gaps collapse like CSS margins: when a run of rows collapses, the
 * next visible row sits below the last visible row above the run by the
 * widest authored gap around or inside the run. Section gaps the designer
 * left below an optional row survive its collapse. A run at the top of the
 * group lands its successor where the run's first row was.
 */
function computeReflowState(spread: Spread, conditionHidden: Set<number>): ReflowState {
  const hiddenUuids = new Set<number>();
  const elementUpdates: ReflowElementUpdate[] = [];
  const rowShiftUpdates: ReflowRowShiftUpdate[] = [];

  if (!spread?.reflowGroups?.length) {
    return { hiddenUuids, elementUpdates, rowShiftUpdates };
  }

  const elementsByUuid = new Map<number, ReflowMember>();
  spread.pages.forEach((page) => {
    page.groups.media?.forEach((element) => {
      if (isReflowMember(element)) {
        elementsByUuid.set(element.uuid, element);
      }
    });
  });

  // Collapse every group before packing any row horizontally: an element can
  // sit in several groups (a "T Tel F Fax M Mobile" line plus an optional
  // "F Fax" pair), and the line must pack against the pair's collapse even when
  // the pair's group is declared after it.
  const groupSnapshots: { group: ReflowGroup; snapshots: RowSnapshot[] }[] = [];

  spread.reflowGroups.forEach((group) => {
    // Resolve every row in this group to its present elements + collapse state
    // + authored bbox. Rows with no resolvable elements are dropped from the
    // reflow walk so they don't contribute to cumulative shift.
    const snapshots: RowSnapshot[] = [];
    group.rows.forEach((row) => {
      const present: ReflowMember[] = [];
      row.elementUuids.forEach((uuid) => {
        const element = elementsByUuid.get(uuid);
        if (element) {
          present.push(element);
        }
      });
      if (present.length === 0) {
        return;
      }
      const currentShift = row.shiftY ?? 0;
      const authoredTop = Math.min(...present.map((e) => getReflowMemberVerticalBounds(e).top - currentShift));
      const authoredBottom = Math.max(...present.map((e) => getReflowMemberVerticalBounds(e).bottom - currentShift));
      snapshots.push({
        row,
        present,
        collapsed: isRowCollapsed(row, present, conditionHidden),
        currentShift,
        authoredTop,
        authoredBottom,
      });
    });

    // Walk in declared order; if author put rows out of y-order, that's their
    // call (matches how Conditions trust author-declared ordering).
    let cumulative = 0;
    let lastVisible: RowSnapshot | undefined;
    let runStart: RowSnapshot | undefined;
    let runEnd: RowSnapshot | undefined;
    let runMaxGap = -Infinity;
    for (let i = 0; i < snapshots.length; i += 1) {
      const snap = snapshots[i];

      if (snap.collapsed) {
        const above = runEnd ?? lastVisible;
        if (above) {
          runMaxGap = Math.max(runMaxGap, snap.authoredTop - above.authoredBottom);
        }
        runStart = runStart ?? snap;
        runEnd = snap;
      } else if (runStart && runEnd) {
        const gapBelowRun = snap.authoredTop - runEnd.authoredBottom;
        // Never negative: a collapse must not push rows below their authored
        // position (only possible when rows are declared out of y-order).
        cumulative += lastVisible
          ? Math.max(0, snap.authoredTop - lastVisible.authoredBottom - Math.max(runMaxGap, gapBelowRun))
          : snap.authoredTop - runStart.authoredTop;
        runStart = undefined;
        runEnd = undefined;
        runMaxGap = -Infinity;
      }

      // `-0` would normalise to `0` for math but Object.is distinguishes them,
      // tripping deep-equal checks in tests and any change-detection in reducers.
      const rawTarget = snap.collapsed ? 0 : -cumulative;
      const targetShift = rawTarget === 0 ? 0 : rawTarget;
      const delta = targetShift - snap.currentShift;

      if (snap.collapsed) {
        snap.row.elementUuids.forEach((uuid) => hiddenUuids.add(uuid));
      }

      if (delta !== 0) {
        snap.present.forEach((element) => {
          elementUpdates.push({ uuid: element.uuid, ...moveReflowMember(element, 0, delta) });
        });
        rowShiftUpdates.push({ groupId: group.id, rowId: snap.row.id, shiftY: targetShift });
      }

      if (!snap.collapsed) {
        lastVisible = snap;
      }
    }

    // Restore authored x positions for rows that had horizontal reflow but no
    // longer do (the flag was toggled off).
    for (let i = 0; i < snapshots.length; i += 1) {
      const snap = snapshots[i];
      if (snap.row.horizontalReflow || !snap.row.elementShiftsX) {
        continue;
      }
      const shiftsX = snap.row.elementShiftsX;
      snap.present.forEach((e) => {
        const shift = shiftsX[String(e.uuid)] ?? 0;
        if (shift !== 0) {
          elementUpdates.push({ uuid: e.uuid, ...moveReflowMember(e, -shift, 0) });
        }
      });
      const emptyShifts: Record<string, number> = {};
      Object.keys(shiftsX).forEach((k) => {
        emptyShifts[k] = 0;
      });
      const existing = rowShiftUpdates.find((u) => u.groupId === group.id && u.rowId === snap.row.id);
      if (existing) {
        existing.elementShiftsX = emptyShifts;
      } else {
        rowShiftUpdates.push({ groupId: group.id, rowId: snap.row.id, elementShiftsX: emptyShifts });
      }
    }

    groupSnapshots.push({ group, snapshots });
  });

  groupSnapshots.forEach(({ group, snapshots }) => {
    // Horizontal reflow: for non-collapsed rows with horizontalReflow enabled,
    // pack elements left-to-right so each starts where the previous one ends.
    for (let i = 0; i < snapshots.length; i += 1) {
      const snap = snapshots[i];
      const isAligned =
        (snap.row.horizontalAlign === 'center' || snap.row.horizontalAlign === 'right') &&
        typeof snap.row.alignWidth === 'number' &&
        Number.isFinite(snap.row.alignWidth);
      if (snap.collapsed || !snap.row.horizontalReflow || (snap.present.length < 2 && !isAligned)) {
        continue;
      }
      const shiftsX = snap.row.elementShiftsX ?? {};
      // Persisted per-element gaps (captured when horizontal reflow was enabled).
      // Legacy rows that were enabled before gaps were captured won't have them;
      // for those we fall back to deriving the gap from the authored layout
      // (authoredX of this element minus the authored end of the previous one),
      // which recovers the designer spacing as long as authored positions are
      // intact (they are — shifts are stripped above).
      const gaps = snap.row.elementGaps;
      const withAuthored = snap.present.map((e) => ({
        element: e,
        authoredX: getReflowMemberLeft(e) - (shiftsX[String(e.uuid)] ?? 0),
      }));
      withAuthored.sort((a, b) => a.authoredX - b.authoredX);

      // Dividers (separators) are shown only when BOTH nearest non-divider
      // neighbours in visual order are present & non-empty; otherwise hide them
      // (visibility only) and drop them from packing so the rest closes up.
      const dividerSet = new Set(snap.row.dividerUuids ?? []);
      if (dividerSet.size > 0) {
        const order = withAuthored.map((w) => w.element);
        const isPresent = (el: ReflowMember) => hasVisibleContent(el, conditionHidden);
        const nearestNonDivider = (from: number, step: number): ReflowMember | undefined => {
          for (let k = from; k >= 0 && k < order.length; k += step) {
            if (!dividerSet.has(order[k].uuid)) {
              return order[k];
            }
          }
          return undefined;
        };
        order.forEach((el, idx) => {
          if (!dividerSet.has(el.uuid)) {
            return;
          }
          const before = nearestNonDivider(idx - 1, -1);
          const after = nearestNonDivider(idx + 1, 1);
          if (!before || !after || !isPresent(before) || !isPresent(after)) {
            hiddenUuids.add(el.uuid);
          }
        });
      }

      // An element that renders nothing must not reserve its width or its
      // authored gap: a blank First Name has to let Last Name slide into its
      // place (GAPI-45466). `hasVisibleContent` also covers condition-hidden
      // members, which previously kept packing a hole open too. Dividers are
      // excluded here because their visibility is decided above by neighbour
      // presence, not by their own text (a "|" is never blank).
      const skippedUuids = new Set<number>();
      withAuthored.forEach(({ element }) => {
        if (!dividerSet.has(element.uuid) && !hasVisibleContent(element, conditionHidden)) {
          skippedUuids.add(element.uuid);
        }
      });
      const isSkipped = (uuid: number) => hiddenUuids.has(uuid) || skippedUuids.has(uuid);

      // Pack only the visible elements (hidden dividers and blank fields
      // reserve no width/gap).
      const packable = withAuthored.filter((w) => !isSkipped(w.element.uuid));

      // Anchor at the row's leftmost AUTHORED x, not at the first surviving
      // element's own x. If the leading element is skipped, the survivor has to
      // move left into the vacated slot; anchoring on `packable[0]` would pin it
      // to where it already is and close no gap at all.
      const rowAnchorX = withAuthored[0].authoredX;

      // Fallback gaps for legacy rows (enabled before elementGaps was captured):
      // measure each element against its immediate AUTHORED predecessor,
      // INCLUDING members that end up skipped. Deriving against the previous
      // *packable* element instead would fold the skipped element's width into
      // the gap, so the vacated space would be preserved and nothing would
      // close up.
      const derivedGaps = new Map<number, number>();
      for (let k = 1; k < withAuthored.length; k += 1) {
        const prev = withAuthored[k - 1];
        derivedGaps.set(
          withAuthored[k].element.uuid,
          Math.max(0, withAuthored[k].authoredX - (prev.authoredX + getReflowMemberPackWidth(prev.element, snap.row))),
        );
      }

      const newShiftsX: Record<string, number> = {};
      let cursor = rowAnchorX;
      // First pass: target x of every packable element, packed from the anchor.
      const targets: number[] = [];
      for (let j = 0; j < packable.length; j += 1) {
        const { element } = packable[j];
        let gap = 0;
        if (j > 0) {
          gap = gaps ? gaps[String(element.uuid)] ?? 0 : derivedGaps.get(element.uuid) ?? 0;
        }
        const t = j === 0 ? rowAnchorX : cursor + gap;
        targets.push(t);
        cursor = t + getReflowMemberPackWidth(element, snap.row);
      }
      // Alignment: shift the whole packed run within the authored span.
      let alignOffset = 0;
      if (isAligned && packable.length > 0) {
        const free = (snap.row.alignWidth as number) - (cursor - rowAnchorX);
        alignOffset = snap.row.horizontalAlign === 'center' ? free / 2 : free;
      }
      for (let j = 0; j < packable.length; j += 1) {
        const { element, authoredX } = packable[j];
        // Keep the designer-authored gap before each element instead of packing
        // flush, so changing one element's content re-sticks the next at
        // prevEnd + gap.
        const targetX = alignOffset === 0 ? targets[j] : targets[j] + alignOffset;
        const shiftX = targetX - authoredX;
        const rawShift = shiftX === 0 ? 0 : shiftX;
        newShiftsX[String(element.uuid)] = rawShift;

        // Text sets its `x` directly to the absolute target (as before this change) —
        // going through a delta (target - current + current) reintroduces floating-point
        // drift that the pre-existing tests pin to exact values. Non-text members have
        // no single settable "left" field (a line has none at all, and a rotated
        // rectangle/image's bbox left isn't its raw `x`), so they move by delta instead.
        if (isMediaText(element)) {
          if (element.x !== targetX) {
            elementUpdates.push({ uuid: element.uuid, x: targetX });
          }
        } else {
          const dx = targetX - getReflowMemberLeft(element);
          if (dx !== 0) {
            elementUpdates.push({ uuid: element.uuid, ...moveReflowMember(element, dx, 0) });
          }
        }
      }

      // Skipped members (hidden dividers, blank fields) are dropped from
      // packing, so they keep whatever x they last had. Reset them to their
      // authored x and record a 0 shift, so that when they reappear
      // authoredX (= x - shift) resolves back to the authored position instead
      // of a stale packed one (which would drift them).
      //
      // Blank fields are deliberately NOT added to `hiddenUuids`: a blank text
      // renders nothing anyway, and marking it hidden would make
      // getPersonalizationErrors skip it, letting a blank MANDATORY field pass
      // checkout validation.
      withAuthored.forEach(({ element, authoredX }) => {
        if (!isSkipped(element.uuid)) {
          return;
        }
        newShiftsX[String(element.uuid)] = 0;
        if (isMediaText(element)) {
          if (element.x !== authoredX) {
            elementUpdates.push({ uuid: element.uuid, x: authoredX });
          }
        } else {
          const dx = authoredX - getReflowMemberLeft(element);
          if (dx !== 0) {
            elementUpdates.push({ uuid: element.uuid, ...moveReflowMember(element, dx, 0) });
          }
        }
      });

      const oldShiftsX = snap.row.elementShiftsX;
      const shiftsChanged =
        !oldShiftsX ||
        Object.keys(newShiftsX).length !== Object.keys(oldShiftsX).length ||
        Object.keys(newShiftsX).some((k) => newShiftsX[k] !== oldShiftsX[k]);
      if (shiftsChanged) {
        const existing = rowShiftUpdates.find((u) => u.groupId === group.id && u.rowId === snap.row.id);
        if (existing) {
          existing.elementShiftsX = newShiftsX;
        } else {
          rowShiftUpdates.push({ groupId: group.id, rowId: snap.row.id, elementShiftsX: newShiftsX });
        }
      }
    }
  });

  return { hiddenUuids, elementUpdates, rowShiftUpdates };
}

export default computeReflowState;
